import type { MentionableEntity } from '../components/RichTextField';
import { formatTimelineItemLabelForBadge } from './timeline-date-format';

export interface MentionableEntitiesApiResponse {
  characters?: Array<{ id: string; name: string; documentId: string; documentTitle?: string }>;
  locations?: Array<{ id: string; name: string; documentId: string; documentTitle?: string }>;
  chapters?: Array<{ id: string; title: string; documentId: string; documentTitle: string; order: number }>;
  storylines?: Array<{ id: string; title: string; documentId: string; documentTitle: string }>;
  scenes?: Array<{ id: string; title: string; documentId: string; documentTitle: string; order: number }>;
  resources?: Array<{ id: string; title: string }>;
  timelineItems?: Array<{
    id: string;
    name: string;
    start_date?: string;
    start_precision?: string;
    end_date?: string | null;
    end_precision?: string | null;
  }>;
  documents?: Array<{ id: string; title: string }>;
  users?: Array<{ id: string; email: string }>;
}

export function mapMentionableEntitiesFromApiResponse(data: MentionableEntitiesApiResponse): MentionableEntity[] {
  return [
    ...(data.users || []).map((u) => ({
      id: u.id,
      label: u.email,
      type: 'user' as const,
    })),
    ...(data.characters || []).map((c) => ({
      id: c.id,
      label: c.name,
      type: 'character' as const,
      documentId: c.documentId,
      ...(c.documentTitle ? { documentTitle: c.documentTitle } : {}),
    })),
    ...(data.locations || []).map((l) => ({
      id: l.id,
      label: l.name,
      type: 'location' as const,
      documentId: l.documentId,
      ...(l.documentTitle ? { documentTitle: l.documentTitle } : {}),
    })),
    ...(data.chapters || []).map((ch) => ({
      id: ch.id,
      label: `${ch.order} ${ch.title} < ${ch.documentTitle}`,
      type: 'manual_chapter' as const,
      documentId: ch.documentId,
      documentTitle: ch.documentTitle,
      order: ch.order,
    })),
    ...(data.storylines || []).map((s) => ({
      id: s.id,
      label: `${s.title} < ${s.documentTitle}`,
      type: 'storyline' as const,
      documentId: s.documentId,
      documentTitle: s.documentTitle,
    })),
    ...(data.scenes || []).map((s) => ({
      id: s.id,
      label: `${s.order} ${s.title} < ${s.documentTitle}`,
      type: 'scene' as const,
      documentId: s.documentId,
      documentTitle: s.documentTitle,
      order: s.order,
    })),
    ...(data.resources || []).map((r) => ({
      id: r.id,
      label: r.title,
      type: 'resource' as const,
    })),
    ...(data.timelineItems || []).map((t) => ({
      id: t.id,
      label:
        t.start_date != null && t.start_precision != null
          ? formatTimelineItemLabelForBadge({
              name: t.name,
              start_date: t.start_date,
              start_precision: t.start_precision,
              end_date: t.end_date ?? null,
              end_precision: t.end_precision ?? null,
            })
          : t.name,
      type: 'timeline_item' as const,
    })),
    ...(data.documents || []).map((d) => ({
      id: d.id,
      label: d.title,
      type: 'document' as const,
      documentId: d.id,
    })),
  ];
}
